import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TagIcon from '@mui/icons-material/Tag';
import { ModuleContentTooltip } from 'components/Common/MUIStyledComponents';
import {
  CountryPop,
  QueryRow,
  RailChip,
  RailSeg,
  RecentRow,
  RecentsMenu,
  RunActions,
  Tone,
  recentMenuItems,
} from 'components/ScreeningComposer/ComposerParts';
import ScreeningWait, {
  WaitBox,
  WaitColumn,
  WaitToolbar,
} from 'components/ScreeningComposer/ScreeningWait';
import { DialogLayer } from 'components/ScreeningComposer/ScreeningDialog';
import FiltersModal from 'components/ScreeningComposer/FiltersModal';
import type { FilterChip } from 'components/ScreeningComposer/ResultsToolbar';
import StaleNotice from 'components/ScreeningComposer/StaleNotice';
import type {
  ListCatalogRow,
  SavedFilter,
  SavedFilterKind,
  Scope,
  ScopeKey,
} from 'components/ScreeningComposer/types';
import { useWidening } from 'components/ScreeningComposer/useWidening';
import {
  LIST_KEYS,
  countScope,
  emptyScope,
  savedMatches,
  shortDate,
} from 'components/ScreeningComposer/utils';
import { fetchRiskConfig, RiskConfig } from '../searchComponents/AIEvaluate';
import EntityScreeningResultsTable from '../tables/entityTable/EntityScreeningResultsTable';
import type { EntityTableExportContext } from '../tables/entityTable/entityTableFilterParams';
import {
  EntityScreenRerunReason,
  EntityScreenSubject,
  resolveEntityScreenSubject,
  topHitSource,
} from '../tables/entityTable/entityHitsPagination';
import EntityVerdictBoxes from './EntityVerdictBoxes';
import 'resources/styles/screening-composer.css';

// The screen's filters: lens, source country, agency and list, picked in the
// Filters modal import and export screening use. They narrow the lists the
// screen runs against.
export interface EntityScreenFilters {
  scope: Scope;
  // The filters the screen on show ran with.
  shownScope: Scope;
  listRows: ListCatalogRow[];
  loading: boolean;
  recordTotal: number | null;
  savedFilters: SavedFilter[];
  onCreateSaved: (
    kind: SavedFilterKind,
    name: string,
    scope: Scope,
  ) => Promise<SavedFilter | null>;
  onUpdateSaved: (filter: SavedFilter, scope: Scope) => Promise<void>;
  onDeleteSaved: (filter: SavedFilter) => Promise<void>;
  // `rerun` re-runs the screen on show with the new filters.
  onApply: (scope: Scope, rerun: boolean) => void;
}

interface EntityScreeningViewProps {
  inputs: any;
  setInputs: (next: any) => void;
  state: any;
  setState?: (next: any) => void;
  results: any;
  ownershipResults?: any;
  aiSums?: any;
  aiLoading?: boolean;
  hasRunOnce: boolean;
  page: number;
  setPage: any;
  handleSubmit: any;
  entityFilterOptions?: any;
  screenFilters?: EntityScreenFilters;
  unfilteredMatchTotal?: number | null;
  tableResetSignal?: number;
  isInteractionDisabled: boolean;
  isWhiteLabel: boolean;
  isRestricted: boolean;
  entityInputMode: 'details' | 'record';
  onEnterDetailsMode: () => void;
  onEnterRecordMode: () => void;
  onSaveTicket: (
    name: string,
    share: string[],
    frequency: string,
  ) => Promise<boolean>;
  savingTicket?: boolean;
  setExportFileNameOpen: (open: boolean) => void;
  onExportContext?: (context: EntityTableExportContext) => void;
  adverseMedia: any;
  adverseMediaError: string | null;
  adverseMediaLoading: boolean;
  beneficialOwnership: any;
  beneficialOwnershipError: string | null;
  beneficialOwnershipLoading: boolean;
  onRunAdverseMedia: (subject: EntityScreenSubject) => void;
  onRunBeneficialOwnership: (subject: EntityScreenSubject) => void;
  onOpenDate?: () => void;
  onClearDate?: () => void;
  // Why the current screen ran: only a new subject is saved as a recent screen.
  screenReason?: EntityScreenRerunReason;
  recentScreensStorageKey: string;
}

type EntityPopover = 'recents' | 'country' | 'address' | 'save';

interface RecentEntityScreen {
  id: string;
  entityName: string;
  inputMode: 'details' | 'record';
  status: 'High' | 'Medium' | 'Low' | 'Unknown';
  matchCount: number;
  screenedAt: string;
  country: string;
  address: string;
  archived: boolean;
}

const WAIT_BOXES: WaitBox[] = [
  {
    label: 'Sanctions & Denied Parties',
    glyph: ShieldOutlinedIcon,
    tail: '60%',
    eval: true,
  },
  {
    label: 'Beneficial Ownership',
    glyph: PeopleOutlineIcon,
    tail: '44%',
    eval: true,
  },
  {
    label: 'Adverse Media',
    glyph: ArticleOutlinedIcon,
    tail: '70%',
    eval: true,
  },
];

const CHIP_GROUP: Partial<Record<ScopeKey, string>> = {
  lens: 'Lens:',
  country: 'Source country:',
  agency: 'Agency:',
  list: 'List:',
};

const NO_FILTERS = emptyScope();

const WAIT_COLUMNS: WaitColumn[] = [
  { label: 'Entity Name', width: 0 },
  { label: 'Type', className: 'psc-skel--country' },
  { label: 'Country', className: 'psc-skel--country' },
  { label: 'Lens', className: 'psc-skel--code' },
  { label: 'List Name', width: 1 },
  { label: 'Match Score', className: 'psc-skel--code' },
  { label: 'Detected', className: 'psc-skel--date' },
];

const WAIT_TOOLBAR: WaitToolbar = {
  placeholder: 'Filter these matches…',
  tabs: ['All Matches', 'Ownership'],
  actions: [
    {
      label: 'Export',
      icon: <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />,
    },
  ],
};

const readRecentScreens = (storageKey: string): RecentEntityScreen[] => {
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.slice(0, 5) : [];
  } catch (_error) {
    return [];
  }
};

const normalizeRecentStatus = (status: any): RecentEntityScreen['status'] => {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('high')) return 'High';
  if (normalized.includes('medium')) return 'Medium';
  if (normalized.includes('low') || normalized.includes('clear')) return 'Low';
  return 'Unknown';
};

const recentTone = (status: RecentEntityScreen['status']): Tone =>
  status === 'Unknown'
    ? 'none'
    : (status.toLowerCase() as 'high' | 'medium' | 'low');

const recentMeta = (screen: RecentEntityScreen) =>
  screen.inputMode === 'record'
    ? 'Record ID'
    : [screen.country, screen.address].filter(Boolean).join(' · ') ||
      'Any country';

const EntityScreeningView: React.FC<EntityScreeningViewProps> = ({
  inputs,
  setInputs,
  state,
  setState,
  results,
  ownershipResults,
  aiSums,
  aiLoading,
  hasRunOnce,
  page,
  setPage,
  handleSubmit,
  entityFilterOptions,
  screenFilters,
  unfilteredMatchTotal,
  tableResetSignal = 0,
  isInteractionDisabled,
  isWhiteLabel,
  isRestricted,
  entityInputMode,
  onEnterDetailsMode,
  onEnterRecordMode,
  onSaveTicket,
  savingTicket = false,
  setExportFileNameOpen,
  onExportContext,
  adverseMedia,
  adverseMediaError,
  adverseMediaLoading,
  beneficialOwnership,
  beneficialOwnershipError,
  beneficialOwnershipLoading,
  onRunAdverseMedia,
  onRunBeneficialOwnership,
  onOpenDate,
  onClearDate,
  screenReason = 'newSubject',
  recentScreensStorageKey,
}) => {
  const [openPop, setOpenPop] = useState<EntityPopover | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tableTabRequest, setTableTabRequest] = useState<{
    tab: 'All Matches' | 'Ownership';
    nonce: number;
  } | null>(null);
  const [tableSearchRequest, setTableSearchRequest] = useState('');
  const [riskConfig, setRiskConfig] = useState<RiskConfig | null>(null);
  const [riskConfigFailed, setRiskConfigFailed] = useState(false);
  const riskConfigRequested = useRef(false);
  const [pendingRecentRun, setPendingRecentRun] =
    useState<RecentEntityScreen | null>(null);
  const [shownScreen, setShownScreen] = useState<RecentEntityScreen | null>(
    null,
  );
  const [screenResults, setScreenResults] = useState<any>(null);
  const [screenOwnershipResults, setScreenOwnershipResults] =
    useState<any>(null);
  // read on the first render, as the import and export composers do: starting empty and
  // filling in from an effect painted the page once without the list and again with it
  const [recentScreens, setRecentScreens] = useState<RecentEntityScreen[]>(() =>
    readRecentScreens(recentScreensStorageKey),
  );
  const [savedFreq, setSavedFreq] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);
  // dialogs mount in this section, above the page's own stacking layer
  const sectionRef = useRef<HTMLElement | null>(null);
  const queryInputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggers = useRef<Partial<Record<EntityPopover, HTMLElement | null>>>(
    {},
  );
  const recordedScreenResultsRef = useRef<any>(null);

  const isRecord = entityInputMode === 'record';
  const queryField = isRecord ? 'record' : 'primary';
  const rawQuery = String(
    isRecord ? inputs?.record || '' : inputs?.primary || '',
  );
  const query = rawQuery.trim();
  const hasQuery = Boolean(query);
  const isScreening = Boolean(hasRunOnce && results?.isLoading);
  const isFreshRun = isScreening && !results?.data?.length;
  const phase: 'idle' | 'run' | 'done' = !hasRunOnce
    ? 'idle'
    : isFreshRun
    ? 'run'
    : 'done';
  const verdictResults = screenResults || results;
  const verdictOwnershipResults = screenOwnershipResults || ownershipResults;
  // Adverse Media and Beneficial Ownership are about the screen's subject: the
  // typed name, or the record a Record ID Search matched — never the ID itself.
  const matchedRecord = topHitSource(verdictResults?.data?.[0]);
  const typedPrimary = String(inputs?.primary || '');
  const screenSubject = useMemo(
    () =>
      resolveEntityScreenSubject({
        inputMode: entityInputMode,
        primary: typedPrimary,
        matchedRecord: matchedRecord,
      }),
    [entityInputMode, typedPrimary, matchedRecord],
  );
  const runAdverseMedia = useCallback(() => {
    if (screenSubject) onRunAdverseMedia(screenSubject);
  }, [screenSubject, onRunAdverseMedia]);
  const runBeneficialOwnership = useCallback(() => {
    if (screenSubject) onRunBeneficialOwnership(screenSubject);
  }, [screenSubject, onRunBeneficialOwnership]);
  const total = Number(
    verdictResults?.data?.[0]?.result?.body?.hits?.total?.value || 0,
  );
  const hasCompletedScreen = hasRunOnce && !isFreshRun && !results?.isLoading;
  const screenDate = aiSums?.[0]?.screenDate || new Date();
  const dateLabel = moment(screenDate).isValid()
    ? moment(screenDate).format('MMM D, YYYY')
    : moment().format('MMM D, YYYY');

  useWidening(composerRef, phase === 'idle' ? 'narrow' : 'wide');

  useEffect(() => {
    if (phase !== 'run') {
      setElapsed(0);
      return undefined;
    }
    const timer = window.setInterval(
      () => setElapsed((value) => value + 1),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (!openPop) return undefined;
    const onDown = (event: MouseEvent) => {
      if (
        shellRef.current &&
        !shellRef.current.contains(event.target as Node)
      ) {
        setOpenPop(null);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenPop(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openPop]);

  const lastPop = useRef<EntityPopover | null>(openPop);
  useEffect(() => {
    const closed = lastPop.current;
    lastPop.current = openPop;
    if (!closed || openPop || closed === 'recents') return;
    const active = document.activeElement;
    if (
      !active ||
      active === document.body ||
      Boolean(shellRef.current?.contains(active))
    ) {
      triggers.current[closed]?.focus();
    }
  }, [openPop]);

  const triggerRef = (name: EntityPopover) => (el: HTMLElement | null) => {
    triggers.current[name] = el;
  };
  const toggle = (name: EntityPopover) =>
    setOpenPop(openPop === name ? null : name);

  useEffect(() => {
    if (phase === 'idle' || phase === 'run') {
      if (screenResults) setScreenResults(null);
      if (screenOwnershipResults) setScreenOwnershipResults(null);
      if (phase === 'run') setSavedFreq(null);
      return;
    }

    if (!screenResults && !results?.isLoading) {
      setScreenResults(results);
    }
    if (!screenOwnershipResults && !ownershipResults?.isLoading) {
      setScreenOwnershipResults(ownershipResults);
    }
  }, [phase, results, ownershipResults, screenResults, screenOwnershipResults]);

  useEffect(() => {
    setRecentScreens(readRecentScreens(recentScreensStorageKey));
  }, [recentScreensStorageKey]);

  useEffect(() => {
    if (phase === 'run') {
      recordedScreenResultsRef.current = null;
      return;
    }
    if (
      phase !== 'done' ||
      results?.isLoading ||
      aiLoading ||
      !query ||
      recordedScreenResultsRef.current === verdictResults
    ) {
      return;
    }

    const recentScreen: RecentEntityScreen = {
      id: `${Date.now()}-${query}`,
      entityName: query,
      inputMode: entityInputMode,
      status: normalizeRecentStatus(aiSums?.[0]?.risk),
      matchCount: total,
      screenedAt: new Date().toISOString(),
      country: String(inputs?.country || ''),
      address: String(inputs?.address || ''),
      archived: Boolean(state?.archived),
    };

    if (screenReason === 'newSubject') {
      setRecentScreens((previous) => {
        const identity =
          `${recentScreen.inputMode}:${recentScreen.entityName}`.toLowerCase();
        const next = [
          recentScreen,
          ...previous.filter(
            (item) =>
              `${item.inputMode}:${item.entityName}`.toLowerCase() !== identity,
          ),
        ].slice(0, 5);
        try {
          window.localStorage.setItem(
            recentScreensStorageKey,
            JSON.stringify(next),
          );
        } catch (_error) {
          return next;
        }
        return next;
      });
    }
    setShownScreen(recentScreen);
    recordedScreenResultsRef.current = verdictResults;
  }, [
    screenReason,
    phase,
    results?.isLoading,
    aiLoading,
    query,
    verdictResults,
    entityInputMode,
    aiSums,
    total,
    inputs?.country,
    inputs?.address,
    state?.archived,
    recentScreensStorageKey,
  ]);

  const updateInput = (field: string, value: string) => {
    setInputs({ ...inputs, [field]: value });
  };

  // A new entity country or archived setting after a screen re-runs the whole screen, once
  // the new value is in state. Adverse Media and Beneficial Ownership bill per check, so
  // they re-run only if they had already been run, and only once the new matches are in:
  // the ownership check reads the records on the Ownership tab.
  const [settingRerun, setSettingRerun] = useState<{
    reason: 'countryOfInterest' | 'archived';
    value: string | boolean;
    stage: 'screen' | 'checks';
    media: boolean;
    ownership: boolean;
  } | null>(null);

  const startSettingRerun = (
    reason: 'countryOfInterest' | 'archived',
    value: string | boolean,
  ) => {
    setSettingRerun({
      reason: reason,
      value: value,
      stage: 'screen',
      // Adverse Media searches the news, not the records: archived records
      // never change it.
      media:
        reason === 'countryOfInterest' &&
        (Boolean(adverseMedia) || adverseMediaLoading),
      ownership: Boolean(beneficialOwnership) || beneficialOwnershipLoading,
    });
  };

  useEffect(() => {
    if (!settingRerun || settingRerun.stage !== 'screen') return;
    const current =
      settingRerun.reason === 'countryOfInterest'
        ? String(inputs?.country || '')
        : Boolean(state?.archived);
    if (current !== settingRerun.value) return;
    setSettingRerun({ ...settingRerun, stage: 'checks' });
    handleSubmit(0, undefined, { reason: settingRerun.reason });
  }, [settingRerun, inputs?.country, state?.archived, handleSubmit]);

  useEffect(() => {
    if (!settingRerun || settingRerun.stage !== 'checks') return;
    if (phase !== 'done' || results?.isLoading || ownershipResults?.isLoading) {
      return;
    }
    setSettingRerun(null);
    if (settingRerun.media) runAdverseMedia();
    if (settingRerun.ownership) runBeneficialOwnership();
  }, [
    settingRerun,
    phase,
    results?.isLoading,
    ownershipResults?.isLoading,
    runAdverseMedia,
    runBeneficialOwnership,
  ]);

  const changeCountry = (value: string) => {
    const was = String(inputs?.country || '');
    updateInput('country', value);
    if (phase !== 'done' || value === was || queryIsStale || !canRun) return;
    startSettingRerun('countryOfInterest', value);
  };

  // A recent screen starts without filters; going back to the screen on show
  // restores the ones it ran with.
  const applyRecentScreen = (
    screen: RecentEntityScreen,
    runAfterSelection: boolean,
    filters: Scope = NO_FILTERS,
  ) => {
    if (isInteractionDisabled) return;
    setOpenPop(null);
    if (screen.inputMode === 'record' && !isRecord) {
      onEnterRecordMode();
    } else if (screen.inputMode === 'details' && isRecord) {
      onEnterDetailsMode();
    }
    setInputs({
      primary: screen.inputMode === 'details' ? screen.entityName : '',
      record: screen.inputMode === 'record' ? screen.entityName : '',
      country: screen.country || '',
      address: screen.address || '',
    });
    if (setState) {
      setState({
        ...state,
        lenses: filters.lens,
        countries: filters.country,
        agencies: filters.agency,
        lists: filters.list,
        date: null,
        endDate: null,
        dateRange: [],
        archived: screen.archived,
      });
    }
    if (runAfterSelection) setPendingRecentRun(screen);
  };

  useEffect(() => {
    if (!pendingRecentRun || isInteractionDisabled) return;
    const expectedQuery = pendingRecentRun.entityName;
    const queryReady =
      pendingRecentRun.inputMode === 'record'
        ? inputs?.record === expectedQuery
        : inputs?.primary === expectedQuery;
    const detailsReady =
      String(inputs?.country || '') === pendingRecentRun.country &&
      String(inputs?.address || '') === pendingRecentRun.address &&
      Boolean(state?.archived) === pendingRecentRun.archived;

    if (
      entityInputMode !== pendingRecentRun.inputMode ||
      !queryReady ||
      !detailsReady
    ) {
      return;
    }

    setPendingRecentRun(null);
    handleSubmit(0);
  }, [
    pendingRecentRun,
    entityInputMode,
    inputs?.primary,
    inputs?.record,
    inputs?.country,
    inputs?.address,
    state?.archived,
    isInteractionDisabled,
    handleSubmit,
  ]);

  const offersRecents = phase === 'done' && recentScreens.length > 0;
  const showRecents = (value: string) => {
    if (offersRecents && value === '') {
      setOpenPop('recents');
    } else if (openPop === 'recents') {
      setOpenPop(null);
    }
  };

  const clearQuery = () => {
    updateInput(queryField, '');
    queryInputRef.current?.focus();
    showRecents('');
  };

  const toggleArchived = () => {
    if (!setState || isInteractionDisabled) return;
    setOpenPop(null);
    const archived = !state?.archived;
    setState({ ...state, archived: archived });
    if (phase !== 'done' || queryIsStale || !canRun) return;
    startSettingRerun('archived', archived);
  };

  const canRun =
    hasQuery &&
    !isInteractionDisabled &&
    !results?.isLoading &&
    phase !== 'run';
  const cannotRunWhy = isInteractionDisabled
    ? 'Entity screening needs a Pro plan or higher'
    : isRecord
    ? 'Paste a BITE Record ID to run'
    : 'Enter a company or person name to run';

  const runScreen = () => {
    if (!canRun) return;
    setOpenPop(null);
    handleSubmit(page);
  };

  const shownQuery = shownScreen?.entityName || query;
  const queryIsStale = Boolean(
    phase === 'done' &&
      shownScreen &&
      (entityInputMode !== shownScreen.inputMode || query !== shownQuery),
  );
  const revert = () => {
    if (shownScreen) {
      applyRecentScreen(shownScreen, false, screenFilters?.shownScope);
    }
  };

  // The filters don't apply to a Record ID Search: it looks up one exact
  // record, as with country and address.
  const filters = screenFilters?.scope || NO_FILTERS;
  const filterCount = isRecord ? 0 : countScope(filters, false);
  const savedFilterName = screenFilters?.savedFilters.find(
    (filter) => filter.type === 'entity' && savedMatches(filter, filters),
  )?.filterName;
  const canOpenFilters =
    Boolean(screenFilters) &&
    !isInteractionDisabled &&
    !isRecord &&
    phase !== 'run';
  const openFilters = () => {
    if (!canOpenFilters) return;
    setOpenPop(null);
    setFiltersOpen(true);
  };
  // The screen on show re-runs with the new filters. Before the first screen,
  // or while a new name waits to be run, they apply to the next run.
  const applyFilters = (next: Scope) => {
    setFiltersOpen(false);
    screenFilters?.onApply(
      next,
      phase === 'done' && !queryIsStale && hasQuery && !isInteractionDisabled,
    );
  };
  const filterChips: FilterChip[] = [];
  if (!isRecord) {
    LIST_KEYS.forEach((dim) => {
      filters[dim].forEach((value) => {
        filterChips.push({
          key: `${dim}:${value}`,
          group: CHIP_GROUP[dim] || '',
          label: value,
          onRemove: () =>
            applyFilters({
              ...filters,
              [dim]: filters[dim].filter((item) => item !== value),
            }),
        });
      });
    });
  }
  const filterPhrase = `${filterCount} ${
    filterCount === 1 ? 'filter' : 'filters'
  }`;

  useEffect(() => {
    if (
      !hasCompletedScreen ||
      isRestricted ||
      riskConfig ||
      riskConfigRequested.current
    ) {
      return;
    }
    riskConfigRequested.current = true;
    setRiskConfigFailed(false);
    const load = async () => {
      const next = await fetchRiskConfig();
      riskConfigRequested.current = false;
      setRiskConfig(next || null);
      setRiskConfigFailed(!next);
    };
    load();
  }, [hasCompletedScreen, isRestricted, riskConfig]);

  const scrollToResults = (
    tab: 'All Matches' | 'Ownership',
    search?: string,
  ) => {
    if (search !== undefined) setTableSearchRequest(search);
    setTableTabRequest((previous) => ({
      tab: tab,
      nonce: (previous?.nonce || 0) + 1,
    }));
    window.requestAnimationFrame(() => {
      document
        .getElementById('psc-results')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const saveTicket = async (
    name: string,
    share: string[],
    frequency: string,
  ) => {
    const saved = await onSaveTicket(name, share, frequency);
    if (saved) setSavedFreq(frequency);
    return saved;
  };

  let statusLine = '';
  if (queryIsStale) {
    const back = `Esc returns to ${shownQuery}`;
    statusLine = hasQuery
      ? `Enter runs a new screen · ${back}`
      : `${cannotRunWhy} · ${back}`;
  } else if (phase === 'done' && hasCompletedScreen) {
    statusLine = savedFreq
      ? `Saved to Saved Work · ${
          savedFreq === 'Once'
            ? 'saved once'
            : `re-screens ${savedFreq.toLowerCase()}`
        }`
      : `${total.toLocaleString()} ${
          total === 1 ? 'match' : 'matches'
        } · ${dateLabel}`;
  }

  const disabledRail = isInteractionDisabled || phase === 'run';
  const stageClass =
    phase === 'idle' ? '' : phase === 'run' ? ' is-run' : ' is-done';
  const runLine = `Screening ${query || 'this entity'} against ${
    filterCount ? filterPhrase : 'every lens, source country, agency and list'
  }.`;

  return (
    <DialogLayer.Provider value={sectionRef}>
      <section
        ref={sectionRef}
        className='psc psc--embedded'
        aria-label='Entity screening'
      >
        <div className='psc-page'>
          <div className='psc-actions'>
            <ModuleContentTooltip
              title={
                <p className='module-header-tooltip-text'>
                  Search entities by name or BITE Record ID, then review
                  sanctions, ownership and adverse-media signals in one place.
                </p>
              }
              placement='bottom'
              arrow
            >
              <button
                type='button'
                className='psc-btn psc-btn--icon psc-btn--ghost'
                aria-label='Help'
              >
                <HelpOutlineIcon sx={{ fontSize: 17 }} />
              </button>
            </ModuleContentTooltip>
          </div>

          <div className={`psc-stage${stageClass}`}>
            <div className='psc-top'>
              <div className='psc-hero'>
                <h1>Screen an entity</h1>
                <p>
                  Sanctions, watchlists, exclusion lists and ownership chains,
                  in one pass.
                </p>
              </div>
            </div>

            <div ref={composerRef} className='psc-composer'>
              <div className='psc-shell' ref={shellRef}>
                <QueryRow
                  glyph={isRecord ? TagIcon : PersonOutlineIcon}
                  inputId='psc-entity-q'
                  label={isRecord ? 'BITE Record ID' : 'Company or person name'}
                  placeholder={
                    isRecord
                      ? 'Paste a BITE Record ID'
                      : 'Company or person name'
                  }
                  value={rawQuery}
                  onChange={(value) => {
                    updateInput(queryField, value);
                    showRecents(value);
                  }}
                  inputRef={(el) => {
                    queryInputRef.current = el;
                    triggers.current.recents = el;
                  }}
                  onFocus={() => showRecents(rawQuery)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      runScreen();
                    } else if (event.key === 'Escape' && queryIsStale) {
                      event.preventDefault();
                      revert();
                    } else if (
                      event.key === 'ArrowDown' &&
                      openPop === 'recents'
                    ) {
                      event.preventDefault();
                      recentMenuItems(menuRef.current)[0]?.focus();
                    }
                  }}
                  onClear={phase === 'run' ? undefined : clearQuery}
                  clearLabel='Clear screening query'
                  disabled={isInteractionDisabled || phase === 'run'}
                  isCode={isRecord}
                  expanded={offersRecents ? openPop === 'recents' : undefined}
                  controls={
                    openPop === 'recents' ? 'psc-entity-recents' : undefined
                  }
                  actions={
                    <RunActions
                      phase={phase}
                      canRun={canRun}
                      cannotRunWhy={cannotRunWhy}
                      onRun={runScreen}
                      newQuery={queryIsStale}
                      ticket={
                        isWhiteLabel || isScreening
                          ? null
                          : {
                              canSave:
                                !isInteractionDisabled && hasCompletedScreen,
                              defaultName: shownQuery,
                              savedFreq: savedFreq,
                              saving: savingTicket,
                              matchCount: total,
                              onSave: saveTicket,
                              open: openPop === 'save',
                              triggerRef: triggerRef('save'),
                              onToggle: () => toggle('save'),
                              onClose: () => setOpenPop(null),
                            }
                      }
                    />
                  }
                  menu={
                    openPop === 'recents' && (
                      <RecentsMenu
                        id='psc-entity-recents'
                        menuRef={menuRef}
                        onExitTop={() => queryInputRef.current?.focus()}
                      >
                        {recentScreens.map((screen) => (
                          <RecentRow
                            key={`menu-${screen.id}`}
                            title={screen.entityName}
                            meta={recentMeta(screen)}
                            tone={recentTone(screen.status)}
                            pill={
                              screen.status === 'Unknown'
                                ? 'Not evaluated'
                                : screen.status
                            }
                            count={`${screen.matchCount.toLocaleString()} ${
                              screen.matchCount === 1 ? 'match' : 'matches'
                            }`}
                            when={shortDate(
                              moment(screen.screenedAt).valueOf(),
                            )}
                            onPick={() => applyRecentScreen(screen, true)}
                            className='psc-recent--menu'
                          />
                        ))}
                      </RecentsMenu>
                    )
                  }
                />

                <div className='psc-rail'>
                  <RailSeg
                    label='Screening input mode'
                    options={[
                      { id: 'details', label: 'Entity name' },
                      { id: 'record', label: 'Record ID' },
                    ]}
                    value={entityInputMode}
                    onChange={(id) => {
                      if (id === entityInputMode) return;
                      setOpenPop(null);
                      if (id === 'record') {
                        onEnterRecordMode();
                      } else {
                        onEnterDetailsMode();
                      }
                    }}
                    disabled={disabledRail}
                  />
                  <span className='psc-vsep' />
                  {isRecord ? (
                    <span className='psc-rail-note'>
                      Country, address and filters don&apos;t apply to an exact
                      record
                    </span>
                  ) : (
                    <>
                      <div className='psc-pos'>
                        <RailChip
                          label='Entity country'
                          value={inputs?.country || 'Any'}
                          set={Boolean(inputs?.country)}
                          triggerRef={triggerRef('country')}
                          onClick={() => toggle('country')}
                          expanded={openPop === 'country'}
                          disabled={disabledRail}
                        />
                        {openPop === 'country' && (
                          <CountryPop
                            inputId='psc-entity-country'
                            dialogLabel='Entity country'
                            searchPlaceholder='Search countries'
                            value={inputs?.country || ''}
                            noneLabel='No country'
                            note='Country narrows which records match. The screen still runs against every list.'
                            onPick={(value) => {
                              changeCountry(value);
                              setOpenPop(null);
                            }}
                          />
                        )}
                      </div>
                      <div className='psc-pos'>
                        <RailChip
                          label='Address'
                          value={inputs?.address || 'Not set'}
                          set={Boolean(inputs?.address)}
                          triggerRef={triggerRef('address')}
                          onClick={() => toggle('address')}
                          expanded={openPop === 'address'}
                          disabled={disabledRail}
                        />
                        {openPop === 'address' && (
                          <div
                            className='psc-pop psc-pop--field'
                            role='dialog'
                            aria-label='Address or location'
                          >
                            <div className='psc-pop-head'>
                              <label
                                htmlFor='psc-entity-address'
                                className='psc-label'
                              >
                                ADDRESS OR LOCATION
                              </label>
                              <input
                                id='psc-entity-address'
                                className='psc-field'
                                autoFocus
                                value={inputs?.address || ''}
                                onChange={(event) =>
                                  updateInput('address', event.target.value)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') setOpenPop(null);
                                }}
                                placeholder='Street, city or region'
                                disabled={isInteractionDisabled}
                              />
                            </div>
                            <div className='psc-pop-note'>
                              Address narrows which records match. The screen
                              still runs against every list.
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <RailChip
                    label='Archived data'
                    value={state?.archived ? 'On' : 'Off'}
                    set={Boolean(state?.archived)}
                    pressed={Boolean(state?.archived)}
                    onClick={toggleArchived}
                    disabled={disabledRail}
                    title='Archived data surfaces records that have been removed from active data sets, for historical reference.'
                  />
                  <span className='psc-status' role='status'>
                    {statusLine}
                  </span>
                </div>
              </div>
            </div>

            <div className='psc-bottom'>
              {phase === 'idle' && (
                <div className='psc-recents'>
                  {recentScreens.length > 0 && (
                    <>
                      <h2 className='psc-h2'>RECENT SCREENS</h2>
                      {recentScreens.map((screen) => (
                        <RecentRow
                          key={screen.id}
                          title={screen.entityName}
                          meta={recentMeta(screen)}
                          tone={recentTone(screen.status)}
                          pill={
                            screen.status === 'Unknown'
                              ? 'Not evaluated'
                              : screen.status
                          }
                          count={`${screen.matchCount.toLocaleString()} ${
                            screen.matchCount === 1 ? 'match' : 'matches'
                          }`}
                          when={shortDate(moment(screen.screenedAt).valueOf())}
                          onPick={() => applyRecentScreen(screen, true)}
                          disabled={isInteractionDisabled}
                        />
                      ))}
                    </>
                  )}
                  <p className='psc-empty-line'>
                    {isRecord ? (
                      "Record IDs look like BL-…-…. Copy one from any match's detail panel."
                    ) : filterCount ? (
                      <>
                        Screens run against{' '}
                        <button
                          type='button'
                          className='psc-link'
                          onClick={openFilters}
                          disabled={!canOpenFilters}
                        >
                          your {filterPhrase}
                        </button>
                        , not every list.
                      </>
                    ) : (
                      <>
                        A name is enough — every screen runs against all lists.{' '}
                        <button
                          type='button'
                          className='psc-link'
                          onClick={openFilters}
                          disabled={!canOpenFilters}
                        >
                          Narrow it by filter
                        </button>{' '}
                        — pick lenses, source countries, agencies or lists
                        before you run.
                      </>
                    )}
                  </p>
                </div>
              )}

              {phase === 'run' && (
                <ScreeningWait
                  runLine={runLine}
                  secs={elapsed}
                  showEval={!isRestricted}
                  riskLabel='Sanctions & Denied Parties'
                  boxes={WAIT_BOXES}
                  columns={WAIT_COLUMNS}
                  toolbar={WAIT_TOOLBAR}
                />
              )}

              {phase === 'done' && (
                <div className='psc-results'>
                  <h2 className='psc-sr'>Screening results</h2>
                  {queryIsStale && shownScreen && (
                    <StaleNotice
                      kind='query'
                      shownFor={shownQuery}
                      changedOptions={[]}
                      canRun={canRun}
                      onRun={runScreen}
                      onRevert={revert}
                    />
                  )}
                  {!isRestricted && (
                    <EntityVerdictBoxes
                      query={(isRecord && screenSubject?.name) || shownQuery}
                      results={verdictResults}
                      ownershipResults={verdictOwnershipResults}
                      aiSums={aiSums}
                      aiLoading={aiLoading}
                      entityFilterOptions={entityFilterOptions}
                      adverseMedia={adverseMedia}
                      adverseMediaError={adverseMediaError}
                      adverseMediaLoading={adverseMediaLoading}
                      beneficialOwnership={beneficialOwnership}
                      beneficialOwnershipError={beneficialOwnershipError}
                      beneficialOwnershipLoading={beneficialOwnershipLoading}
                      isInteractionDisabled={isInteractionDisabled}
                      riskConfig={riskConfig}
                      riskConfigFailed={riskConfigFailed}
                      filtersActive={
                        countScope(
                          screenFilters?.shownScope || NO_FILTERS,
                          false,
                        ) > 0
                      }
                      unfilteredMatchCount={unfilteredMatchTotal}
                      onRunAdverseMedia={runAdverseMedia}
                      onRunBeneficialOwnership={runBeneficialOwnership}
                      onViewAllMatches={(search) =>
                        scrollToResults('All Matches', search)
                      }
                      onViewOwnership={() => scrollToResults('Ownership')}
                    />
                  )}
                  <EntityScreeningResultsTable
                    key={`entity-results-table-${tableResetSignal}`}
                    appearance='screening-v2'
                    handleSubmit={handleSubmit}
                    setPage={setPage}
                    page={page}
                    results={results}
                    ownershipResults={ownershipResults}
                    searchTerm={
                      isRecord ? screenSubject?.name || '' : inputs?.primary
                    }
                    entityFilterOptions={entityFilterOptions}
                    screenFilterControls={{
                      count: filterCount,
                      label: savedFilterName,
                      chips: filterChips,
                      onOpen: canOpenFilters ? openFilters : undefined,
                      onClear: () => applyFilters(emptyScope()),
                    }}
                    isStatic={Boolean(results?.complete)}
                    onExport={(context) => {
                      onExportContext?.(context);
                      setExportFileNameOpen(true);
                    }}
                    activeTabRequest={tableTabRequest}
                    requestedSearchQuery={tableSearchRequest}
                    onOpenAsOfDate={onOpenDate}
                    onClearAsOfDate={onClearDate}
                    asOfLabel={
                      state?.dateRange?.length
                        ? state.dateRange.join(' – ')
                        : ''
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {filtersOpen && screenFilters && (
          <FiltersModal
            scope={screenFilters.scope}
            listKind='entity'
            countryTitle='Source country'
            listRows={screenFilters.listRows}
            loading={screenFilters.loading}
            recordTotal={screenFilters.recordTotal}
            savedFilters={screenFilters.savedFilters}
            onCreateSaved={screenFilters.onCreateSaved}
            onUpdateSaved={screenFilters.onUpdateSaved}
            onDeleteSaved={screenFilters.onDeleteSaved}
            applyLabel={
              phase === 'done' && !queryIsStale
                ? 'Re-run screen'
                : 'Apply filters'
            }
            onApply={applyFilters}
            onCancel={() => setFiltersOpen(false)}
          />
        )}
      </section>
    </DialogLayer.Provider>
  );
};

export default EntityScreeningView;
